"use client";

import { useContext, useEffect, useId, useRef } from "react";
import ModalContext from "src/components/common/Modal/ModalContext";
import { activeDismissGuards, confirmDismissGuards } from "src/lib/DismissGuards";

export interface DismissGuardOptions {
  /** Activates the guard while the surface has unsaved changes. */
  when: boolean;
  /**
   * Whether closing the surrounding dialog loses the input. Turn off for dialogs that keep their
   * draft when closed, so only navigation and page unload ask.
   * @default true
   */
  guardDialogClose?: boolean;
  /**
   * Paths the draft survives navigating to, for an editor spread over several pages: moving
   * between them keeps the draft, so only leaving all of them asks.
   */
  allowNavigation?: (to: string) => boolean;
}

export const useDismissGuard = (options: DismissGuardOptions) => {
  const { when, guardDialogClose = true, allowNavigation } = options;
  const id = useId();
  const modal = useContext(ModalContext);
  const modalKey = guardDialogClose ? modal?.guardKey : undefined;
  const allowNavigationRef = useRef(allowNavigation);
  allowNavigationRef.current = allowNavigation;

  useEffect(() => {
    if (!when) return;
    activeDismissGuards.set(id, {
      modal: modalKey,
      allowNavigation: (to) => allowNavigationRef.current?.(to) ?? false,
    });
    return () => {
      activeDismissGuards.delete(id);
    };
  }, [id, when, modalKey]);

  return {
    /**
     * For custom Cancel buttons that close a dialog directly: resolves true when it is fine to
     * discard (nothing unsaved, or the person agreed).
     */
    confirmDismiss: () =>
      when ? confirmDismissGuards({ force: true, modal: modalKey }) : Promise.resolve(true),
  };
};
